
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useTranslation } from "react-i18next";
import type { ProfileTimelineSection, TemplateTimelineSection, TimelineSection } from "@/types";

interface TimelineData {
  profileTimeline: any;
  profileSections: (ProfileTimelineSection & {
    template_timeline_sections: TemplateTimelineSection;
  })[];
}

interface UseTimelineResult {
  timelineSections: TimelineSection[];
  isLoading: boolean;
  error: string | null;
  timelineExists: boolean;
}

export const useTimeline = (userId: string, birthdate?: string): UseTimelineResult => {
  const { i18n } = useTranslation();

  const { data: timelineData, isLoading, error } = useQuery({
    queryKey: ['timeline', userId, birthdate],
    queryFn: async (): Promise<TimelineData | null> => {
      if (!userId) return null;

      // Get timeline
      const { data: profileTimeline, error: timelineError } = await supabase
        .from('profile_timeline')
        .select('*')
        .eq('user_id', userId)
        .maybeSingle();

      if (timelineError) {
        console.error('Timeline query error:', timelineError);
        throw new Error(`Timeline query failed: ${timelineError.message}`);
      }

      if (!profileTimeline) {
        return null;
      }

      // Get timeline sections with template data
      const { data: profileSections, error: sectionsError } = await supabase
        .from('profile_timeline_sections')
        .select(`
          id,
          profile_timeline_id,
          template_timeline_section_id,
          name,
          name_en,
          description,
          description_en,
          cosmic_aspect,
          cosmic_aspect_en,
          icon,
          is_active,
          sort_order,
          created_at,
          updated_at,
          template_timeline_sections (
            id,
            name,
            name_en,
            slug,
            age_from,
            age_to,
            description,
            description_en,
            cosmic_aspect,
            cosmic_aspect_en,
            icon,
            sort_order
          )
        `)
        .eq('profile_timeline_id', profileTimeline.id)
        .eq('is_active', true)
        .order('sort_order');

      if (sectionsError) {
        console.error('Sections query error:', sectionsError);
        throw new Error(`Sections query failed: ${sectionsError.message}`);
      }

      const formattedSections = (profileSections || []).map(section => {
        const templateData = section.template_timeline_sections;
        return {
          ...section,
          template_timeline_sections: templateData
        };
      }).filter(section => section.template_timeline_sections);

      return {
        profileTimeline,
        profileSections: formattedSections as (ProfileTimelineSection & {
          template_timeline_sections: TemplateTimelineSection;
        })[]
      };
    },
    enabled: !!userId,
    retry: 1,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: false,
    refetchOnMount: false
  });

  // Convert to timeline sections
  const getTimelineSections = (): TimelineSection[] => {
    if (!timelineData?.profileSections || !birthdate) {
      return [];
    }

    const birthYear = new Date(birthdate).getFullYear();
    const isEnglish = i18n.language === 'en';

    const sections = timelineData.profileSections.map((section) => {
      const template = section.template_timeline_sections;
      
      if (!template) {
        console.warn('No template data for section:', section.id);
        return null;
      }

      const name = isEnglish ? (template.name_en || template.name) : template.name;
      const description = isEnglish ? (template.description_en || template.description) : template.description;
      const cosmicAspect = isEnglish ? (template.cosmic_aspect_en || template.cosmic_aspect) : template.cosmic_aspect;

      return {
        id: section.id,
        name: section.name || name,
        name_en: template.name_en,
        slug: template.slug,
        age_from: template.age_from,
        age_to: template.age_to,
        description: section.description || description,
        description_en: template.description_en,
        cosmic_aspect: section.cosmic_aspect || cosmicAspect,
        cosmic_aspect_en: template.cosmic_aspect_en,
        icon: section.icon || template.icon,
        created_at: section.created_at,
        updated_at: section.updated_at,
        yearFrom: birthYear + template.age_from,
        yearTo: birthYear + template.age_to,
        isExpanded: false
      } as TimelineSection;
    }).filter(Boolean) as TimelineSection[];
    
    return sections;
  };

  const timelineSections = getTimelineSections();
  const timelineExists = !!timelineData?.profileTimeline;

  return {
    timelineSections,
    isLoading,
    error: error?.message || null,
    timelineExists
  };
};
